import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import clsx from 'clsx'
import Dialog from 'components/dialog'
import { useIsMobile } from 'lib/hooks'
import CollectionsBookmarkOutlinedIcon from 'components/material-symbol/icons/CollectionsBookmark'
import BookOutlinedIcon from 'components/material-symbol/icons/Book'
import EventNoteOutlinedIcon from 'components/material-symbol/icons/EventNote'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import PlaceIcon from 'components/material-symbol/icons/Place'
import ScheduleOutlinedIcon from 'components/material-symbol/icons/Schedule'
import NavigateNextOutlinedIcon from 'components/material-symbol/icons/NavigateNext'
import RedeemIcon from 'components/material-symbol/icons/Redeem'
import ConfirmationNumberOutlinedIcon from 'components/material-symbol/icons/ConfirmationNumber'
import RouterLinkButton from 'components/router-link-button'
import EventDescription from 'pages/pga-coach/bookings/events/event-details-dialog/event-details-modal/event-description'
import SubviewPageHeader from 'pages/pga-coach/bookings/events/event-details-dialog/subview-page-header'
import IconBadge from 'pages/pga-coach/bookings/events/event-details-dialog/event-details-modal/icon-badge'
import { GET_LESSON_TYPE_DESCRIPTION } from 'pages/pga-coach/bookings/booking-types/queries'
import DetailsIconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/icon-badge'
import ActionButtons from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/action-buttons'
import { useStyles } from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/styles'
import { useStyles as useEventDetailsStyles } from 'pages/pga-coach/bookings/events/event-details-dialog/event-details-modal/styles'
import useIconAccessor from 'components/lesson-type-category/icons'
import pluralize from 'lib/pluralize'
import { PGA_COM_HOST } from 'env'
import { toFullTextDayDateWithTimezone } from 'utils/dateUtils'
import { durationText } from 'utils/durationUtils'
import {
  buildLessonPackBookingUrls,
  getLessonPackActionVisibility,
  getLessonPackStatusLabel,
} from './lesson-pack-action-utils'

const getCoachProfileSlug = coach => {
  if (coach?.coachProfile?.slug) {
    return coach.coachProfile.slug
  }

  if (coach?.externalId) {
    return coach.externalId
  }

  const bookingUrlMatch = coach?.bookingUrl?.match(/\/coach\/([^/]+)/)
  return bookingUrlMatch?.[1] ?? null
}

const getCoachProfileUrl = coach => {
  const slug = getCoachProfileSlug(coach)
  return slug ? `${PGA_COM_HOST}/coach/${slug}` : null
}

const getLocationMapsUrl = locationName =>
  locationName
    ? `https://maps.google.com/?q=${encodeURIComponent(locationName)}`
    : null

const getLessonCreditsWithEnrollment = lessonCredits =>
  lessonCredits
    .filter(credit => credit.enrollment)
    .sort(
      (a, b) =>
        new Date(b.enrollment.lesson.startDateTime) -
        new Date(a.enrollment.lesson.startDateTime),
    )

const LessonPackHistorySubview = ({ lessonPack, onBack }) => {
  const classes = useStyles()
  const { lessonCredits = [], lessonType } = lessonPack
  const lessonCreditsWithEnrollment = getLessonCreditsWithEnrollment(
    lessonCredits,
  )

  return (
    <DialogContent className={classes.contentRoot}>
      <SubviewPageHeader
        eventTitle={lessonType?.title}
        pageTitle="Lesson pack history"
        onBack={onBack}
      />
      <Box className={classes.detailsCard}>
        {lessonCreditsWithEnrollment.map((lessonCredit, index) => (
          <Box
            key={`${lessonCredit.enrollment?.lesson?.startDateTime}-${index}`}
            className={classes.detailRow}
          >
            <DetailsIconBadge icon={EventNoteOutlinedIcon} />
            <Box minWidth={0}>
              <Typography className={classes.detailPrimary}>
                {toFullTextDayDateWithTimezone(
                  lessonCredit.enrollment?.lesson?.startDateTime,
                )}
              </Typography>
              <Typography className={classes.detailSecondary}>
                {lessonCredit.enrollment?.student?.firstName}{' '}
                {lessonCredit.enrollment?.student?.lastName}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </DialogContent>
  )
}

const LessonPackGiftCallout = ({ lessonPack, onGiftClick, onClose }) => {
  const classes = useStyles()
  const {
    id: lessonPackId,
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  } = lessonPack
  const { showGift } = getLessonPackActionVisibility({
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  })

  if (!showGift) {
    return null
  }

  const handleGiftClick = () => {
    onClose()
    onGiftClick(lessonPackId)
  }

  return (
    <Box className={clsx(classes.actionsCallout, classes.giftActionsCallout)}>
      <Typography className={classes.actionsCalloutTitle}>
        Share your unused lessons?
      </Typography>
      <Box className={classes.actionsCalloutButtons}>
        <Button
          onClick={handleGiftClick}
          variant="outlined"
          color="primary"
          startIcon={<RedeemIcon />}
        >
          Gift Lesson Pack
        </Button>
      </Box>
    </Box>
  )
}

const LessonPackSessionCallout = ({ lessonPack }) => {
  const classes = useStyles()
  const {
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  } = lessonPack
  const { showBook, showBuyAnother } = getLessonPackActionVisibility({
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  })
  const bookingUrls = buildLessonPackBookingUrls(lessonType, remainingCredits)

  if (!showBook && !showBuyAnother) {
    return null
  }

  return (
    <Box className={classes.actionsCallout}>
      <Typography className={classes.actionsCalloutTitle}>
        Ready for your next session?
      </Typography>
      <Box className={classes.actionsCalloutButtons}>
        {showBook && bookingUrls ? (
          <RouterLinkButton
            to={bookingUrls.bookSessionUrl}
            variant="contained"
            color="primary"
            startIcon={<EventNoteOutlinedIcon />}
          >
            Book
          </RouterLinkButton>
        ) : null}
        {showBuyAnother && bookingUrls ? (
          <RouterLinkButton
            to={bookingUrls.buyMoreVouchersUrl}
            variant="outlined"
            color="primary"
            startIcon={<ConfirmationNumberOutlinedIcon />}
          >
            Buy Another Pack
          </RouterLinkButton>
        ) : null}
      </Box>
    </Box>
  )
}

const LessonPackActionsCallout = ({ lessonPack, onGiftClick, onClose }) => {
  const {
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  } = lessonPack
  const { showBook, showBuyAnother, showGift } = getLessonPackActionVisibility({
    lessonType,
    totalCredits,
    remainingCredits,
    isUsedOrExpired,
  })

  if (!showBook && !showBuyAnother && !showGift) {
    return null
  }

  return (
    <>
      <LessonPackSessionCallout lessonPack={lessonPack} />
      <LessonPackGiftCallout
        lessonPack={lessonPack}
        onGiftClick={onGiftClick}
        onClose={onClose}
      />
    </>
  )
}

const ConsumerLessonPackDetailsContent = ({
  lessonPack,
  description,
  onOpenHistory,
  onGiftClick,
  onClose,
}) => {
  const classes = useStyles()
  const eventClasses = useEventDetailsStyles()
  const accessor = useIconAccessor()
  const {
    expiresAt,
    totalCredits,
    remainingCredits,
    lessonCredits = [],
    lessonType,
    isUsedOrExpired,
  } = lessonPack
  const category = accessor[lessonType?.category] || accessor.OTHER
  const locationName = lessonType?.locations?.[0]?.name
  const locationMapsUrl = getLocationMapsUrl(locationName)
  const coachName = lessonType?.coach?.name
  const coachProfileUrl = getCoachProfileUrl(lessonType?.coach)
  const lessonsPurchased = totalCredits ?? lessonCredits.length
  const lessonCreditsWithEnrollment = getLessonCreditsWithEnrollment(
    lessonCredits,
  )
  const trimmedDescription = description?.trim()
  const historyCount = lessonCreditsWithEnrollment.length

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <ActionButtons
            setIsOpen={onClose}
            bookingType={lessonType}
            readOnly
          />
          <Box className={classes.heroContent}>
            <Chip
              label={getLessonPackStatusLabel({
                remainingCredits,
                expiresAt,
                isUsedOrExpired,
              })}
              size="small"
              className={classes.statusChip}
            />
            <Typography className={classes.heroTitle}>
              {lessonType?.title}
            </Typography>
            <Typography className={classes.heroSubtitle}>
              {category.text}
            </Typography>
          </Box>
          <Box className={classes.heroPrice}>
            <Typography className={classes.heroPriceLabel}>
              REMAINING
            </Typography>
            <Typography className={classes.heroPriceValue}>
              {remainingCredits}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        <Grid container spacing={0} className={classes.contentGrid}>
          <Grid item xs={12} md={5} className={classes.aboutColumn}>
            <Box className={classes.mainColumn}>
              <Box>
                <Typography className={classes.sectionLabel}>
                  About this lesson pack
                </Typography>
                {trimmedDescription ? (
                  <EventDescription description={trimmedDescription} />
                ) : (
                  <Typography className={classes.detailSecondary}>
                    Lesson pack credits can be used to book sessions with your
                    coach through PGA Coach.
                  </Typography>
                )}
              </Box>
              {onGiftClick ? (
                <LessonPackActionsCallout
                  lessonPack={lessonPack}
                  onGiftClick={onGiftClick}
                  onClose={onClose}
                />
              ) : null}
            </Box>
          </Grid>
          <Grid item xs={12} md={7} className={classes.detailsColumn}>
            <Box className={classes.sidebar}>
              <Box className={classes.detailsCard}>
                <Typography className={classes.sectionLabel}>
                  Lesson pack details
                </Typography>
                <Box className={classes.detailRow}>
                  <DetailsIconBadge icon={CollectionsBookmarkOutlinedIcon} />
                  <Box>
                    <Typography className={classes.detailPrimary}>
                      Lessons purchased
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {lessonsPurchased} {pluralize(lessonsPurchased, 'lesson')}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.detailRow}>
                  <DetailsIconBadge icon={BookOutlinedIcon} />
                  <Box>
                    <Typography className={classes.detailPrimary}>
                      Sessions remaining
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {remainingCredits} {pluralize(remainingCredits, 'lesson')}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.detailRow}>
                  <DetailsIconBadge icon={AccessTimeIcon} />
                  <Box>
                    <Typography className={classes.detailPrimary}>
                      Expiration
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {expiresAt
                        ? toFullTextDayDateWithTimezone(expiresAt)
                        : 'These packs do not expire'}
                    </Typography>
                  </Box>
                </Box>
                {coachName && coachProfileUrl ? (
                  <Box
                    component="a"
                    href={coachProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      classes.detailRow,
                      classes.clickableDetailRow,
                    )}
                  >
                    <DetailsIconBadge icon={PersonOutlineIcon} />
                    <Box flex={1} minWidth={0}>
                      <Typography className={classes.detailPrimary}>
                        Coach
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {coachName}
                      </Typography>
                    </Box>
                    <NavigateNextOutlinedIcon color="action" />
                  </Box>
                ) : coachName ? (
                  <Box className={classes.detailRow}>
                    <DetailsIconBadge icon={PersonOutlineIcon} />
                    <Box>
                      <Typography className={classes.detailPrimary}>
                        Coach
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {coachName}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
                {locationName && locationMapsUrl ? (
                  <Box
                    component="a"
                    href={locationMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={clsx(
                      classes.detailRow,
                      classes.clickableDetailRow,
                    )}
                  >
                    <DetailsIconBadge icon={PlaceIcon} />
                    <Box flex={1} minWidth={0}>
                      <Typography className={classes.detailPrimary}>
                        Location
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {locationName}
                      </Typography>
                    </Box>
                    <NavigateNextOutlinedIcon color="action" />
                  </Box>
                ) : locationName ? (
                  <Box className={classes.detailRow}>
                    <DetailsIconBadge icon={PlaceIcon} />
                    <Box minWidth={0}>
                      <Typography className={classes.detailPrimary}>
                        Location
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {locationName}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
                {lessonType?.durationInMinutes ? (
                  <Box className={classes.detailRow}>
                    <DetailsIconBadge icon={ScheduleOutlinedIcon} />
                    <Box>
                      <Typography className={classes.detailPrimary}>
                        Session length
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {durationText(lessonType.durationInMinutes)}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
              </Box>
              {historyCount > 0 ? (
                <Box
                  className={eventClasses.infoCard}
                  onClick={onOpenHistory}
                  role="button"
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onOpenHistory()
                    }
                  }}
                >
                  <IconBadge icon={EventNoteOutlinedIcon} variant="action" />
                  <Box className={eventClasses.infoCardContent}>
                    <Typography className={eventClasses.infoCardTitle}>
                      {historyCount} {pluralize(historyCount, 'session')}
                    </Typography>
                    <Typography className={eventClasses.infoCardSubtitle}>
                      View lesson pack history
                    </Typography>
                  </Box>
                  <NavigateNextOutlinedIcon color="action" />
                </Box>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  )
}

const ConsumerLessonPackDetailsDialog = ({
  isOpen,
  setIsOpen,
  lessonPack,
  onGiftClick,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const [view, setView] = useState('detail')
  const lessonTypeId = lessonPack?.lessonType?.id
  const { data: descriptionData } = useQuery(GET_LESSON_TYPE_DESCRIPTION, {
    variables: { id: lessonTypeId },
    skip: !isOpen || !lessonTypeId,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!isOpen) {
      setView('detail')
    }
  }, [isOpen])

  if (!lessonPack) {
    return null
  }

  const description =
    descriptionData?.lessonTypes?.[0]?.description ??
    lessonPack.lessonType?.description

  const handleClose = () => {
    setView('detail')
    setIsOpen(false)
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={view === 'history'}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      classes={{ paper: classes.dialogPaper }}
    >
      {() => (
        <>
          {view === 'detail' ? (
            <ConsumerLessonPackDetailsContent
              lessonPack={lessonPack}
              description={description}
              onOpenHistory={() => setView('history')}
              onGiftClick={onGiftClick}
              onClose={handleClose}
            />
          ) : null}
          {view === 'history' ? (
            <LessonPackHistorySubview
              lessonPack={lessonPack}
              onBack={() => setView('detail')}
            />
          ) : null}
        </>
      )}
    </Dialog>
  )
}

export default ConsumerLessonPackDetailsDialog
